import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  Tooltip,
  LinearProgress,
  Switch,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  GitHub,
  PlayArrow,
  Assessment,
  AccessTime,
  PlayCircleOutline,
  Rule,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Repository } from '../../store/api/githubApi';
import PrChecksDialog from './PrChecksDialog';
import { fmtMoney } from '../../pages/scans/utils';

interface RepositoryCardProps {
  repository: Repository;
  onScan: (repoId: number) => void;
  scanStatus?: string | null;
  scanMessage?: string;
  latestScanId?: number | null;
  onTogglePrGate?: (repoId: number, enabled: boolean) => void;
  isTogglingPrGate?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function scoreColor(score: number | null): string {
  if (score === null) return '#64748b';
  if (score >= 75) return '#22c55e';
  if (score >= 50) return '#f59e0b';
  return '#ef4444';
}

function scoreLabel(score: number | null): string {
  if (score === null) return '—';
  if (score >= 75) return 'HEALTHY';
  if (score >= 50) return 'MODERATE';
  return 'CRITICAL';
}

function formatLoc(loc: number): string {
  if (loc >= 1_000_000) return `${(loc / 1_000_000).toFixed(1)}M`;
  if (loc >= 1000) return `${(loc / 1000).toFixed(1)}k`;
  return String(loc);
}

function formatCount(n: number): string {
  if (n >= 1000) return n.toLocaleString();
  return String(n);
}

// ── Scan progress messages ────────────────────────────────────────────────────

const SCAN_MESSAGES: Record<string, string[]> = {
  queued: ['Waiting in queue…', 'Getting ready to scan…', 'Almost starting…'],
  scanning: [
    'Checking your code…',
    'Running security analysis…',
    'Detecting complexity issues…',
    'Scanning dependencies…',
    'Looking for leaked secrets…',
    'Analysing code quality…',
    'Reading file structure…',
    'Counting lines of code…',
  ],
  analysing: [
    'AI is reading your code…',
    'Prioritising issues by impact…',
    'Estimating remediation costs…',
    'Assessing security exposure…',
    'Profiling capacity limits…',
    'Building executive summary…',
    'Ranking issues by business risk…',
    'Almost done…',
  ],
};

const PHASE_COLORS: Record<string, string> = {
  queued: '#94a3b8',
  scanning: '#38bdf8',
  analysing: '#a78bfa',
};

// ── Sub-components ────────────────────────────────────────────────────────────

const ScanProgressPanel: React.FC<{ status: string; backendMessage?: string }> = ({
  status,
  backendMessage,
}) => {
  const fallbackMessages = SCAN_MESSAGES[status] ?? SCAN_MESSAGES.queued;
  const [msgIdx, setMsgIdx] = useState(0);

  useEffect(() => { setMsgIdx(0); }, [status]);

  useEffect(() => {
    if (backendMessage) return;
    const id = setInterval(() => setMsgIdx((i) => (i + 1) % fallbackMessages.length), 2500);
    return () => clearInterval(id);
  }, [fallbackMessages, backendMessage]);

  const displayMessage = backendMessage ?? fallbackMessages[msgIdx];
  const color = PHASE_COLORS[status] ?? '#94a3b8';
  const phase =
    status === 'queued' ? 'Queued'
      : status === 'scanning' ? 'Phase 1 · Static analysis'
        : 'Phase 2 · AI analysis';

  return (
    <Box sx={{ borderRadius: '12px', bgcolor: '#0f172a', border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden' }}>
      <Box sx={{ px: 2, pt: 1.5, pb: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 7, height: 7, borderRadius: '50%', bgcolor: color, flexShrink: 0,
            animation: 'pulse 1.5s ease-in-out infinite',
            '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
          }}
        />
        <Typography sx={{ fontSize: 10, fontWeight: 700, color, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {phase}
        </Typography>
      </Box>

      <Box sx={{ px: 2, pb: 2 }}>
        <Typography
          key={displayMessage}
          sx={{
            fontFamily: 'monospace', fontSize: 12, color, lineHeight: 1.5,
            animation: 'fadeUp 0.4s ease',
            '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'none' } },
          }}
        >
          {'> '}{displayMessage}
        </Typography>
      </Box>

      <LinearProgress
        sx={{
          height: 2, bgcolor: 'rgba(255,255,255,0.06)',
          '& .MuiLinearProgress-bar': { bgcolor: color },
        }}
      />
    </Box>
  );
};

/** Thin 4px progress bar with rounded ends */
const CategoryBar: React.FC<{ label: string; score: number | null }> = ({ label, score }) => {
  const color = scoreColor(score);
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography sx={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: 12.5,
            fontWeight: 700,
            fontFamily: '"JetBrains Mono", "Roboto Mono", monospace',
            color: score === null ? '#cbd5e1' : color,
          }}
        >
          {score ?? '—'}
        </Typography>
      </Box>
      <Box sx={{ height: 4, borderRadius: 2, bgcolor: '#f1f5f9', overflow: 'hidden' }}>
        <Box
          sx={{
            height: '100%',
            width: `${score ?? 0}%`,
            bgcolor: color,
            borderRadius: 2,
            transition: 'width 0.6s ease',
          }}
        />
      </Box>
    </Box>
  );
};

// Shown alongside the Security score bar so real dollar exposure can never
// hide behind a green/high number the way it did before scores["security"]
// combined code-level findings with dependency CVEs (2026-09-18 fix) — a
// number is much harder to misread as "clean" than a lone 0-100 score.
const SecurityExposureNote: React.FC<{ exposureUsd: number | null }> = ({ exposureUsd }) => {
  if (!exposureUsd || exposureUsd <= 0) return null;
  return (
    <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#ef4444', mt: 0.25 }}>
      {fmtMoney(exposureUsd)} in vulnerability exposure
    </Typography>
  );
};

/** Inline issue indicator: ● 2 Critical */
const IssueIndicator: React.FC<{ color: string; count: number | null; label: string }> = ({
  color, count, label,
}) => {
  if (!count) return null;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#334155', lineHeight: 1 }}>
        {count}
      </Typography>
      <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#64748b', lineHeight: 1 }}>
        {label}
      </Typography>
    </Box>
  );
};

// ── Score Ring (SVG) ──────────────────────────────────────────────────────────

const ScoreRing: React.FC<{ score: number | null; size?: number }> = ({ score, size = 60 }) => {
  const color = scoreColor(score);
  const strokeWidth = 3;
  const r = (size - strokeWidth * 2) / 2;
  const circ = 2 * Math.PI * r;
  const dash = score !== null ? (score / 100) * circ : 0;
  const hasStats = score !== null;

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke="#f1f5f9" strokeWidth={strokeWidth}
        />
        {hasStats && (
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={`${dash} ${circ}`}
            strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 0.5s ease' }}
          />
        )}
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", "Roboto Mono", monospace',
            fontSize: size * 0.3,
            fontWeight: 800,
            lineHeight: 1,
            color: hasStats ? '#0f172a' : '#cbd5e1',
          }}
        >
          {hasStats ? score : '—'}
        </Typography>
        {hasStats && (
          <Typography
            sx={{
              fontSize: 7,
              fontWeight: 700,
              color,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              lineHeight: 1,
              mt: 0.25,
            }}
          >
            {scoreLabel(score)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// ── Main card ─────────────────────────────────────────────────────────────────

export const RepositoryCard: React.FC<RepositoryCardProps> = ({
  repository,
  onScan,
  scanStatus,
  scanMessage,
  latestScanId,
  onTogglePrGate,
  isTogglingPrGate,
}) => {
  const navigate = useNavigate();
  const [prChecksOpen, setPrChecksOpen] = useState(false);
  const isRunning = !!scanStatus && !['complete', 'failed'].includes(scanStatus);
  const repoName = repository.full_name.split('/')[1];

  const score = repository.latest_debt_score;
  const hasStats = score !== null;

  const totalIssues =
    (repository.latest_count_critical ?? 0) +
    (repository.latest_count_high ?? 0) +
    (repository.latest_count_medium ?? 0) +
    (repository.latest_count_low ?? 0);

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '22px',
        border: '1px solid #e8ecf1',
        bgcolor: '#ffffff',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06), 0 2px 6px rgba(15, 23, 42, 0.03)',
        transition: 'transform 180ms ease, box-shadow 180ms ease',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 14px 36px rgba(15, 23, 42, 0.09), 0 4px 10px rgba(15, 23, 42, 0.05)',
        },
      }}
    >
      {/* ── Header ── */}
      <CardContent sx={{ pb: 0, px: 2.5, pt: 2.25, '&:last-child': { pb: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
          {/* Repo identity */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '8px',
                  bgcolor: '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <GitHub sx={{ fontSize: 15, color: '#475569' }} />
              </Box>
              <Tooltip title={repoName} placement="top-start">
                <Typography
                  noWrap
                  sx={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: '#0f172a',
                    lineHeight: 1.3,
                  }}
                >
                  {repoName}
                </Typography>
              </Tooltip>
            </Box>
            <Typography
              noWrap
              sx={{
                fontSize: 12,
                color: '#94a3b8',
                pl: '34px', // align under name after icon
                display: 'block',
              }}
            >
              {repository.full_name}
            </Typography>
          </Box>

          {/* Score ring */}
          {!isRunning && <ScoreRing score={score} size={60} />}

          {/* Scanning state — pulsing ring */}
          {isRunning && (
            <Box
              sx={{
                width: 60, height: 60, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(PHASE_COLORS[scanStatus as string] ?? '#94a3b8', 0.08),
                border: '2px solid',
                borderColor: PHASE_COLORS[scanStatus as string] ?? '#94a3b8',
                animation: 'ring 1.8s ease-in-out infinite',
                '@keyframes ring': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
              }}
            >
              <CircularProgress
                size={22}
                thickness={3}
                sx={{ color: PHASE_COLORS[scanStatus as string] ?? '#94a3b8' }}
              />
            </Box>
          )}
        </Box>

        {/* Metadata: language · LOC */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.25, pl: '34px', flexWrap: 'wrap' }}>
          {repository.repo_type === 'iac' ? (
            <Typography sx={{ fontSize: 11.5, color: '#64748b', fontWeight: 500 }}>
              IaC{repository.iac_framework ? ` · ${repository.iac_framework}` : ''}
            </Typography>
          ) : repository.language ? (
            <>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#2563eb', flexShrink: 0 }} />
              <Typography sx={{ fontSize: 11.5, color: '#475569', fontWeight: 500 }}>
                {repository.language}
              </Typography>
            </>
          ) : null}
          {repository.language && repository.latest_loc_total != null && (
            <Typography sx={{ fontSize: 11.5, color: '#94a3b8', mx: 0.25 }}>·</Typography>
          )}
          {repository.latest_loc_total != null && (
            <Typography sx={{ fontSize: 11.5, color: '#64748b', fontWeight: 500 }}>
              {formatLoc(repository.latest_loc_total)} lines
            </Typography>
          )}
        </Box>
      </CardContent>

      {/* ── Body ── */}
      <CardContent sx={{ pt: 1.5, px: 2.5, pb: 0, flexGrow: 1, '&:last-child': { pb: 0 } }}>
        {/* Scan running */}
        {isRunning && (
          <ScanProgressPanel status={scanStatus as string} backendMessage={scanMessage} />
        )}

        {/* Never scanned — ghost preview + CTA */}
        {!isRunning && !hasStats && (
          <>
            <Typography
              sx={{
                fontSize: 10, fontWeight: 700, color: '#94a3b8',
                letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1.25,
              }}
            >
              Category Scores
            </Typography>

            {/* Ghost bars — hint at what will appear */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2, opacity: 0.3 }}>
              {['Security', 'Complexity', 'Coverage'].map((label) => (
                <Box key={label}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>{label}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>—</Typography>
                  </Box>
                  <Box sx={{ height: 4, borderRadius: 2, bgcolor: '#e2e8f0' }} />
                </Box>
              ))}
            </Box>

            {/* Primary CTA */}
            <Box
              sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: 0.5, py: 1.25, px: 2,
                borderRadius: '12px',
                border: '1.5px dashed #e2e8f0',
                textAlign: 'center',
              }}
            >
              <PlayCircleOutline sx={{ fontSize: 26, color: '#2563eb', opacity: 0.7 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                Run your first scan
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
                Get security, complexity, and dependency insights
              </Typography>
            </Box>
          </>
        )}

        {/* Category score bars */}
        {!isRunning && hasStats && (
          <>
            <Typography
              sx={{
                fontSize: 10, fontWeight: 700, color: '#94a3b8',
                letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1.25,
              }}
            >
              Category Scores
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
              <Box>
                {/* Security now combines code-level findings AND dependency
                    CVEs into one score (see scan_scorer.py) — the separate
                    "Dependencies" bar this used to sit next to is gone since
                    it would just show the identical number. */}
                <CategoryBar label="Security" score={repository.latest_score_security} />
                <SecurityExposureNote exposureUsd={repository.latest_security_exposure_usd} />
              </Box>
              {repository.repo_type === 'iac' ? (
                <CategoryBar label="Infrastructure" score={repository.latest_score_complexity} />
              ) : (
                <>
                  <CategoryBar label="Complexity" score={repository.latest_score_complexity} />
                  <CategoryBar label="Coverage" score={repository.latest_score_coverage} />
                </>
              )}
            </Box>

            {/* Issue severity breakdown */}
            {totalIssues > 0 && (
              <Box sx={{ mt: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.75 }}>
                  <Typography
                    sx={{
                      fontSize: 10, fontWeight: 700, color: '#94a3b8',
                      letterSpacing: '0.08em', textTransform: 'uppercase',
                    }}
                  >
                    Issues Found
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      fontFamily: '"JetBrains Mono", "Roboto Mono", monospace',
                      color: '#334155',
                    }}
                  >
                    {formatCount(totalIssues)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                  <IssueIndicator color="#ef4444" count={repository.latest_count_critical} label="Critical" />
                  <IssueIndicator color="#f97316" count={repository.latest_count_high} label="High" />
                  <IssueIndicator color="#f59e0b" count={repository.latest_count_medium} label="Medium" />
                  <IssueIndicator color="#94a3b8" count={repository.latest_count_low} label="Low" />
                </Box>
              </Box>
            )}

            {totalIssues === 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#22c55e' }} />
                <Typography sx={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>
                  No issues found
                </Typography>
              </Box>
            )}
          </>
        )}
      </CardContent>

      {/* ── Spacer to push footer down ── */}
      <Box sx={{ flexGrow: 1 }} />

      {/* ── Footer ── */}
      {!isRunning && (
        <Box sx={{ px: 2.5, pt: 1.25, pb: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTime sx={{ fontSize: 12, color: '#cbd5e1' }} />
            <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
              {repository.latest_scan_at
                ? `Scanned ${new Date(repository.latest_scan_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}`
                : 'Never scanned'}
            </Typography>
          </Box>
          {onTogglePrGate && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Tooltip title="Scan every pull request for net-new issues and post results directly on GitHub">
                  <Typography sx={{ fontSize: 11, color: '#64748b', cursor: 'default' }}>
                    PR gate
                  </Typography>
                </Tooltip>
                {repository.pr_gate_enabled && (
                  <Typography
                    sx={{ fontSize: 11, color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => setPrChecksOpen(true)}
                  >
                    View checks
                  </Typography>
                )}
              </Box>
              <Switch
                size="small"
                checked={!!repository.pr_gate_enabled}
                disabled={!!isTogglingPrGate}
                onChange={(e) => onTogglePrGate(repository.id, e.target.checked)}
                inputProps={{ 'data-onboarding-target': 'pr-gate' } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Action bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.25,
          borderTop: '1px solid #f1f5f9',
        }}
      >
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={latestScanId ? 'View last scan results' : 'No scan results yet'}>
            <span>
              <Button
                size="small"
                startIcon={<Assessment sx={{ fontSize: '16px !important' }} />}
                disabled={!latestScanId}
                onClick={() => latestScanId && navigate(`/scans/${latestScanId}`)}
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: 'none',
                  color: '#475569',
                  borderRadius: '10px',
                  px: 1.25,
                  '&:hover': { bgcolor: '#f8fafc', color: '#2563eb' },
                  '&.Mui-disabled': { color: '#cbd5e1' },
                }}
              >
                Results
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="PR gate policies for this repo">
            <Button
              size="small"
              startIcon={<Rule sx={{ fontSize: '16px !important' }} />}
              onClick={() => navigate(`/repos/${repository.id}/policies`)}
              sx={{
                fontSize: 12,
                fontWeight: 600,
                textTransform: 'none',
                color: '#475569',
                borderRadius: '10px',
                px: 1.25,
                '&:hover': { bgcolor: '#f8fafc', color: '#2563eb' },
              }}
            >
              Policies
            </Button>
          </Tooltip>
        </Box>

        <Button
          size="small"
          variant="contained"
          startIcon={isRunning ? <CircularProgress size={13} color="inherit" /> : <PlayArrow sx={{ fontSize: '16px !important' }} />}
          disabled={!!isRunning}
          onClick={() => onScan(repository.id)}
          data-onboarding-target="repo-scan"
          sx={{
            fontSize: 12.5,
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: '10px',
            px: 2,
            py: 0.75,
            bgcolor: '#2563eb',
            boxShadow: '0 2px 6px rgba(37, 99, 235, 0.25)',
            '&:hover': {
              bgcolor: '#1d4ed8',
              boxShadow: '0 4px 10px rgba(37, 99, 235, 0.35)',
            },
          }}
        >
          {isRunning ? 'Running…' : 'Scan'}
        </Button>
      </Box>

      <PrChecksDialog repoId={repository.id} open={prChecksOpen} onClose={() => setPrChecksOpen(false)} />
    </Card>
  );
};
